export const MAX_BYTES = 500 * 1024
export const DIMS = [1600, 1200, 900, 675, 600] as const
export const QUALITIES = [0.7, 0.5, 0.35] as const

export interface CompressionPick<T> {
  result: T
  fitsUnderCap: boolean
}

/**
 * Pure ladder search: tries `dims x qualities` combinations, largest and
 * highest-quality first, until `encode()` returns something at or under
 * `maxBytes` — and if nothing fits, returns the smallest result found
 * rather than a fixed-quality blob regardless of size (plan 027 design
 * decision 5: the ≤500KB claim must be enforced, not usually true).
 *
 * Deliberately free of any DOM/canvas API so it can be unit-tested in plain
 * Node (see imageCompress.spec.ts) with a fake `encode` — the real canvas
 * encoding lives in compressImageIfNeeded below.
 */
export async function findSmallestUnderCap<T extends { size: number }>(
  maxBytes: number,
  dims: readonly number[],
  qualities: readonly number[],
  encode: (dim: number, quality: number) => Promise<T>,
): Promise<CompressionPick<T>> {
  let best: T | null = null
  for (const dim of dims) {
    for (const quality of qualities) {
      const result = await encode(dim, quality)
      if (!best || result.size < best.size) best = result
      if (result.size <= maxBytes) return { result, fitsUnderCap: true }
    }
  }
  return { result: best as T, fitsUnderCap: false }
}

/**
 * `compress` (renamed from `lowData` — plan 027 step 5b, rules.md-style
 * honest naming): callers decide *when* to compress; this function no
 * longer has an opinion about connection quality. Receipts always pass
 * `compress: true` so a phone photo is downscaled/compressed regardless of
 * the user's low-data setting — B2's ≤500KB requirement is unconditional.
 * Task attachments keep passing `compress: lowData` (unchanged behaviour).
 *
 * Non-image files (PDFs) are never compressed — bounded only by
 * plan_limit_bytes() like every other upload.
 */
export async function compressImageIfNeeded(file: File, compress: boolean): Promise<File> {
  if (!compress || !file.type.startsWith('image/') || file.size <= MAX_BYTES) return file

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch (err) {
    // HEIC (and anything else the browser can't decode client-side) falls
    // through untouched — the upload proceeds at full size and the
    // download route serves it back with its real content type (plan 027
    // step 1). This is the documented fallback, not a missed case.
    console.warn('compressImageIfNeeded: could not decode image, uploading original', err)
    return file
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  const encode = (dim: number, quality: number): Promise<Blob> => {
    const scale = Math.min(1, dim / Math.max(bitmap.width, bitmap.height))
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    // `toBlob` hands back `null` when the browser can't encode the canvas —
    // a high-res phone photo past the device's canvas memory, which is exactly
    // the input this path exists for. Reject rather than resolve `null`: the
    // ladder would otherwise read `.size` off it and throw a TypeError that
    // escapes the fallback below.
    return new Promise((resolve, reject) => canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas_encode_failed'))),
      'image/jpeg',
      quality,
    ))
  }

  let pick: CompressionPick<Blob>
  try {
    pick = await findSmallestUnderCap(MAX_BYTES, DIMS, QUALITIES, encode)
  } catch (err) {
    // Same fallback as an undecodable image above — upload the original bytes
    // untouched rather than failing the upload outright.
    console.warn('compressImageIfNeeded: could not encode image, uploading original', err)
    return file
  }
  const { result: blob, fitsUnderCap } = pick
  if (!fitsUnderCap) {
    console.warn(
      `compressImageIfNeeded: could not compress ${file.name} under ${MAX_BYTES} bytes — smallest achieved ${blob.size} bytes`,
    )
  }
  return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
}
